import React, { useState, useEffect, Fragment, useMemo } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import api from "../../../api";
import swal from "sweetalert";
import { Row, Col, Card, Button, Form, Spinner, Table, Modal } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Select from "react-select";
import SignatureModal from "../Common/SignatureModal";

export const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    height: '42px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#3a7afe' : '#d7dae3',
    boxShadow: state.isFocused ? '0 0 0 1px #3a7afe' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#3a7afe' : '#b0b5c1',
    },
    fontSize: '0.95rem',
    backgroundColor: '#fff',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 12px',
  }),
  input: (base) => ({
    ...base,
    margin: '0px',
    padding: '0px',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const ContractualWorkOrderAdd = () => {
 const navigate = useNavigate();
 const { id } = useParams();
 const [searchParams] = useSearchParams();
 const workOrderFromUrl = searchParams.get("workOrder") || "";
  const projectFromUrl = searchParams.get("project") || "";
  const [currentProject, setCurrentProject] = useState(projectFromUrl);
 const emptyBillTo = {
 name: "",
 address: "",
 phone: "",
 gstNumber: "",
 state: "",
 stateCode: "",
 };
 const emptyShipTo = {
 name: "",
 address: "",
 phone: "",
 state: "",
 stateCode: "",
 projectName: "",
 };

 const normalizeManualSection = (section, fallback) => ({
 ...fallback,
 name: section?.name || "",
 address: section?.address || "",
 phone: section?.phone || "",
 gstNumber: section?.gstNumber || "",
 state: section?.state || "",
 stateCode: section?.stateCode || "",
 ...(Object.prototype.hasOwnProperty.call(fallback, "projectName")
 ? { projectName: section?.projectName || "" }
 : {}),
 });

 const [workOrders, setWorkOrders] = useState([]);
 const [vendors, setVendors] = useState([]);
 const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
 const [offices, setOffices] = useState([]);
 const [loading, setLoading] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);

 const [showSignModal, setShowSignModal] = useState(false);
 const [signType, setSignType] = useState(""); // prepared, verified, authorized

 // Payment Terms state 
 const PAYMENT_STAGES = ["Advance", "On Delivery", "After Installation", "After Testing", "On Completion", "Retention Release", "Supply"];
 const [globalPaymentTerms, setGlobalPaymentTerms] = useState([{ name: "", percentage: "" }]);
 const [globalPaymentMode, setGlobalPaymentMode] = useState("CUSTOM");
 const [showGlobalTermsModal, setShowGlobalTermsModal] = useState(false);
 const globalPaymentTotal = globalPaymentTerms.reduce((sum, t) => sum + Number(t.percentage || 0), 0);

 const [showItemTermsModal, setShowItemTermsModal] = useState(false);
 const [itemTermsIndex, setItemTermsIndex] = useState(null);
 const [itemTerms, setItemTerms] = useState([{ name: "", percentage: "" }]);
 const [itemTermsMode, setItemTermsMode] = useState("CUSTOM");
 const itemTermsTotal = itemTerms.reduce((sum, t) => sum + Number(t.percentage || 0), 0);

 const openItemTerms = (index) => {
 const item = formData.selectedBoqItems[index];
 const itemTermsFromBoq = Array.isArray(item.paymentTerms) ? item.paymentTerms : [];

 if (itemTermsFromBoq.length) {
 // Item has its own terms (from BOQ or manually set)
 const mode = item.paymentMode || "CUSTOM";
 setItemTermsMode(mode);
 // Normalize payment terms based on mode
 setItemTerms(itemTermsFromBoq.map((t) => ({
 name: mode === "CUSTOM" ? (t.name || t.stage || "") : (t.stage || t.name || ""),
 percentage: t.percentage || "",
 })));
 } else if (globalPaymentTerms.filter(t => t.name?.trim()).length > 0) {
 // Fall back to global terms
 setItemTermsMode(globalPaymentMode);
 setItemTerms(globalPaymentTerms.map((t) => ({ ...t })));
 } else {
 setItemTermsMode("CUSTOM");
 setItemTerms([{ name: "", percentage: "" }]);
 }
 setItemTermsIndex(index);
 setShowItemTermsModal(true);
 };

 const saveItemTerms = () => {
 if (itemTermsTotal !== 100) { swal("Invalid", "Total must be 100%", "warning"); return; }
 const cleaned = itemTerms.filter(t => t.name?.trim() || t.percentage).map(t => ({
 name: itemTermsMode === "CUSTOM" ? (t.name || "").trim() : "",
 stage: itemTermsMode === "STANDARD" ? (t.name || "").trim() : "",
 percentage: Number(t.percentage || 0),
 }));
 const updatedItems = [...formData.selectedBoqItems];
 updatedItems[itemTermsIndex] = { ...updatedItems[itemTermsIndex], paymentMode: itemTermsMode, paymentTerms: cleaned };
 setFormData(prev => ({ ...prev, selectedBoqItems: updatedItems }));
 setShowItemTermsModal(false);
 };

 const clearItemTerms = (index) => {
 const updatedItems = [...formData.selectedBoqItems];
 updatedItems[index] = { ...updatedItems[index], paymentMode: "CUSTOM", paymentTerms: [] };
 setFormData(prev => ({ ...prev, selectedBoqItems: updatedItems }));
 };

 const COMPANY_STATE_ALIASES = new Set(["maharashtra", "maharastra"]);

 const normalizeState = (state = "") => state.toString().trim().toLowerCase();

 const getItemsTotal = (items = []) =>
 items.reduce(
 (sum, item) => sum + Number(item.quantity || 0) * Number(item.price || 0),
 0
 );

 const [formData, setFormData] = useState({
 workOrder: workOrderFromUrl,
 vendor: "",
 selectedBoqItems: [],
 paymentTerms: "",
 workDetails: "",
 remarks: "",
 manualBillTo: emptyBillTo,
 manualShipTo: emptyShipTo,
 addGst: false,
 cgst: 0,
 sgst: 0,
 igst: 0,
 subTotal: 0,
 cgstAmount: 0,
 sgstAmount: 0,
 igstAmount: 0,
 taxAmount: 0,
 totalAmount: 0,
 preparedBySign: null,
 verifiedBySign: null,
 authorizedBySign: null,
 });

 useEffect(() => {
 fetchInitialData();
 }, [id]);

 useEffect(() => {
  if (formData.workOrder && workOrders.length > 0) {
   const wo = workOrders.find((w) => String(w._id) === String(formData.workOrder));
   setSelectedWorkOrder(wo || null);
   if (wo) {
    const pId = wo.project?._id || wo.project;
    if (pId && !currentProject) {
     setCurrentProject(String(pId));
    }
   }

   // [OK] Fetch full WO details to get boqItems with paymentTerms
   if (formData.workOrder) {
    api.get(`/work-orders/${formData.workOrder}`)
     .then((res) => {
      if (res.data.success && res.data.data) {
       setSelectedWorkOrder(res.data.data);
       const pId = res.data.data.project?._id || res.data.data.project;
       if (pId && !currentProject) {
        setCurrentProject(String(pId));
       }
      }
     })
     .catch((err) => console.error("Failed to fetch WO details:", err));
   }
  }
 }, [formData.workOrder, workOrders, id, currentProject]);

 useEffect(() => {
 const itemsTotal = getItemsTotal(formData.selectedBoqItems);
 if (itemsTotal > 0) {
 calculateTotals(itemsTotal, formData.vendor);
 }
 }, [formData.selectedBoqItems, formData.vendor, vendors, selectedWorkOrder, offices]);

 const fetchInitialData = async () => {
 try {
 setLoading(true);
 const [woRes, vRes, offRes] = await Promise.all([
 api.get("/work-orders"),
 api.get("/vendors"),
 api.get("/offices").catch(() => ({ data: { success: false, data: [] } })),
 ]);
 if (woRes.data.success) setWorkOrders(woRes.data.data);
 if (vRes.data.success) setVendors(vRes.data.data);
 if (offRes.data?.success && Array.isArray(offRes.data.data)) setOffices(offRes.data.data);

 if (id) {
 const cwoRes = await api.get(`/contractual-work-orders/${id}`);
 if (cwoRes.data.success) {
 const cwo = cwoRes.data.data;
   const cwoProj = cwo.workOrder?.project?._id || cwo.workOrder?.project || "";
   if (cwoProj) {
    setCurrentProject(String(cwoProj));
   }
   if (cwo.workOrder && typeof cwo.workOrder === "object") {
    setSelectedWorkOrder(cwo.workOrder);
   }

   const normalizedItems = (cwo.selectedBoqItems || []).map((it, idx) => {
    // Only treat as manual if explicitly marked manual, boqItemId starts with "manual", or boqItemId is null/empty
    const hasBoqId = Boolean(it.boqItemId && !String(it.boqItemId).startsWith("manual"));
    const isManual = Boolean(it.isManual === true || String(it.boqItemId || "").startsWith("manual") || (!hasBoqId && it.isManual !== false));
    return {
     ...it,
     boqItemId: it.boqItemId || (isManual ? `manual-${Date.now()}-${idx}` : null),
     isManual: isManual,
     wlino: it.wlino || "",
     itemName: it.itemName || "",
     description: it.description || "",
     unit: it.unit || "Nos",
     quantity: it.quantity ?? 1,
     price: it.price ?? 0,
     amount: (Number(it.quantity) || 0) * (Number(it.price) || 0),
     paymentMode: it.paymentMode || cwo.paymentMode || "CUSTOM",
     paymentTerms: Array.isArray(it.paymentTerms) ? it.paymentTerms : [],
    };
   });

 setFormData({
 ...cwo,
 workOrder: cwo.workOrder?._id || cwo.workOrder,
 vendor: cwo.vendor?._id || cwo.vendor,
 selectedBoqItems: normalizedItems,
 manualBillTo: normalizeManualSection(cwo.manualBillTo, emptyBillTo),
 manualShipTo: normalizeManualSection(cwo.manualShipTo, emptyShipTo),
 addGst: Boolean(cwo.cgst || cwo.sgst || cwo.igst),
 });
 // Load structured payment terms
 if (cwo.structuredPaymentTerms?.length) {
 setGlobalPaymentMode(cwo.paymentMode || "CUSTOM");
 setGlobalPaymentTerms(cwo.structuredPaymentTerms.map(t => ({
 name: t.name || t.stage || "",
 percentage: t.percentage || "",
 })));
 }
 }
 }
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to load data", "error");
 } finally {
 setLoading(false);
 }
 };

 const handleBoqSelect = (item, isChecked) => {
 let updatedItems = [...formData.selectedBoqItems];
 if (isChecked) {
 // Check if item has its own payment terms
 const hasItemTerms = Array.isArray(item.paymentTerms) && item.paymentTerms.length > 0;
 
 // If item has terms, use them; otherwise use global terms as fallback
 const paymentMode = hasItemTerms ? (item.paymentMode || "CUSTOM") : globalPaymentMode;
 const sourceTerms = hasItemTerms ? item.paymentTerms : globalPaymentTerms;
 
 // Normalize payment terms to ensure both stage and name fields are properly set
 const paymentTerms = sourceTerms.map(t => ({
 stage: paymentMode === "STANDARD" ? (t.stage || t.name || "") : "",
 name: paymentMode === "CUSTOM" ? (t.name || t.stage || "") : "",
 percentage: t.percentage || 0,
 }));

 updatedItems.push({
 boqItemId: item._id,
 wlino: item.wlino,
 itemName: item.itemName,
 description: item.description,
 unit: item.unit,
 quantity: item.quantity,
 price: item.price,
 // Use item terms if available, otherwise use global terms
 paymentMode: paymentMode,
 paymentTerms: paymentTerms,
 });
 } else {
 updatedItems = updatedItems.filter((i) => i.boqItemId !== item._id);
 }
 setFormData(prev => ({ ...prev, selectedBoqItems: updatedItems }));
 };

  const handleSelectAllBoq = (isChecked) => {
    if (!selectedWorkOrder) return;
    const manualItems = formData.selectedBoqItems.filter((i) => i.isManual);
    let updatedItems = [];
    if (isChecked) {
      const newBoq = selectedWorkOrder.boqItems.map(item => {
        const hasItemTerms = Array.isArray(item.paymentTerms) && item.paymentTerms.length > 0;
        const paymentMode = hasItemTerms ? (item.paymentMode || "CUSTOM") : globalPaymentMode;
        const sourceTerms = hasItemTerms ? item.paymentTerms : globalPaymentTerms;
        
        const paymentTerms = sourceTerms.map(t => ({
          stage: paymentMode === "STANDARD" ? (t.stage || t.name || "") : "",
          name: paymentMode === "CUSTOM" ? (t.name || t.stage || "") : "",
          percentage: t.percentage || 0,
        }));

        return {
          boqItemId: item._id,
          isManual: false,
          wlino: item.wlino,
          itemName: item.itemName,
          description: item.description,
          unit: item.unit,
          quantity: item.quantity,
          price: item.price,
          amount: (Number(item.quantity) || 0) * (Number(item.price) || 0),
          paymentMode: paymentMode,
          paymentTerms: paymentTerms,
        };
      });
      updatedItems = [...newBoq, ...manualItems];
    } else {
      updatedItems = [...manualItems];
    }
    const newSubTotal = updatedItems.reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.price || 0)), 0);
    setFormData(prev => ({ ...prev, selectedBoqItems: updatedItems }));
    calculateTotals(newSubTotal, formData.vendor);
  };

  const handleItemEdit = (index, field, value) => {
    const updatedItems = [...formData.selectedBoqItems];
    const isNumeric = field === "quantity" || field === "price";
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: isNumeric ? (value === "" ? "" : Number(value)) : value,
    };
    if (isNumeric) {
      const q = Number(updatedItems[index].quantity || 0);
      const p = Number(updatedItems[index].price || 0);
      updatedItems[index].amount = q * p;
      const newSubTotal = updatedItems.reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.price || 0)), 0);
      calculateTotals(newSubTotal, formData.vendor);
    }
    setFormData(prev => ({ ...prev, selectedBoqItems: updatedItems }));
  };

  const handleAddManualItem = () => {
    const manualId = "manual_" + Date.now() + "_" + Math.random().toString(36).substring(2, 7);
    const newItem = {
      boqItemId: manualId,
      isManual: true,
      wlino: "",
      itemName: "",
      description: "",
      unit: "Nos",
      quantity: 1,
      price: 0,
      amount: 0,
      paymentMode: globalPaymentMode,
      paymentTerms: globalPaymentTerms.map(t => ({
        stage: globalPaymentMode === "STANDARD" ? (t.stage || t.name || "") : "",
        name: globalPaymentMode === "CUSTOM" ? (t.name || t.stage || "") : "",
        percentage: t.percentage || 0,
      })),
    };
    const updated = [...formData.selectedBoqItems, newItem];
    setFormData(prev => ({
      ...prev,
      selectedBoqItems: updated,
    }));
  };

  const handleRemoveItem = (index) => {
    const updatedItems = formData.selectedBoqItems.filter((_, i) => i !== index);
    const newSubTotal = updatedItems.reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.price || 0)), 0);
    setFormData(prev => ({ ...prev, selectedBoqItems: updatedItems }));
    calculateTotals(newSubTotal, formData.vendor);
  };

 const getProjectOfficeState = () => {
 const office = selectedWorkOrder?.project?.office;
 let st = "";
 if (office && typeof office === "object") {
 st = office.state || "";
 if (!st && office.address) {
 const addr = office.address.toLowerCase();
 if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
 else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
 }
 } else if (office) {
 const officeId = office._id || office;
 const found = offices.find((o) => String(o._id) === String(officeId));
 if (found) {
 st = found.state || "";
 if (!st && found.address) {
 const addr = found.address.toLowerCase();
 if (addr.includes("maharashtra") || addr.includes("mumbai")) st = "Maharashtra";
 else if (addr.includes("uttar pradesh") || addr.includes("noida")) st = "Uttar Pradesh";
 }
 }
 }
 return st;
 };

 const calculateTotals = (subTotal, vendorId) => {
 let cgst = 0, sgst = 0, igst = 0;
 let cgstAmount = 0, sgstAmount = 0, igstAmount = 0;

 const numericSubTotal = Number(subTotal || 0);

 if (vendorId) {
 const vendor = vendors.find((v) => String(v._id) === String(vendorId));
 const hasGst = Boolean(vendor?.gst && String(vendor.gst).trim());
 const vendorState = normalizeState(vendor?.state);
 const officeState = normalizeState(getProjectOfficeState());

 if (!hasGst) {
 cgst = 0;
 sgst = 0;
 igst = 0;
 } else if (vendorState && officeState && vendorState === officeState) {
 cgst = 9; sgst = 9;
 cgstAmount = (numericSubTotal * 9) / 100;
 sgstAmount = (numericSubTotal * 9) / 100;
 } else {
 igst = 18;
 igstAmount = (numericSubTotal * 18) / 100;
 }
 }

 const taxAmount = cgstAmount + sgstAmount + igstAmount;
 const totalAmount = numericSubTotal + taxAmount;

 setFormData((prev) => ({
 ...prev,
 addGst: Boolean(cgst || sgst || igst),
 subTotal: numericSubTotal,
 cgst, sgst, igst,
 cgstAmount, sgstAmount, igstAmount,
 taxAmount,
 totalAmount,
 }));
 };

 const handleSaveSignature = async (file) => {
 try {
 const reader = new FileReader();
 reader.readAsDataURL(file);
 reader.onloadend = () => {
 const base64data = reader.result;
 setFormData(prev => ({
 ...prev,
 [`${signType}BySign`]: {
 ...prev[`${signType}BySign`],
 sign: base64data,
 date: new Date()
 }
 }));
 setShowSignModal(false);
 swal("Success", "Signature captured", "success");
 };
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to capture signature", "error");
 }
 };

 const handlePreviewPDF = async () => {
 if (!id) return;
 try {
 setPreviewingPdf(true);
 const token = localStorage.getItem("token");
 const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
 const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
 const url = `${cleanBaseUrl}/api/contractual-work-orders/download-pdf/${id}`;

 const response = await fetch(url, {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Accept': 'application/pdf'
 }
 });

 if (!response.ok) throw new Error("Failed to generate PDF");

 const blob = await response.blob();
 const fileURL = window.URL.createObjectURL(
 new Blob([blob], { type: "application/pdf" })
 );

 window.open(fileURL, "_blank");
 } catch (err) {
 console.error("PDF preview error:", err);
 swal("Error", "Failed to preview PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!formData.workOrder) {
 return swal("Error", "Please select a Work Order", "error");
 }
 if (!formData.vendor) {
 return swal("Error", "Please select a Vendor", "error");
 }
 if (formData.subTotal <= 0) {
 return swal("Error", "Please add BOQ items with a valid value (total must be greater than 0)", "error");
 }
 const invalidManual = formData.selectedBoqItems.find(i => i.isManual && !String(i.itemName || "").trim());
 if (invalidManual) {
 return swal("Validation", "Please enter an Item Name for all manual items", "warning");
 }
 try {
 const filledGlobal = globalPaymentTerms.filter(t => t.name?.trim() || t.percentage);
 if (filledGlobal.length > 0 && globalPaymentTotal !== 100) {
 return swal("Invalid Payment Terms", "Global payment terms must total 100%", "warning");
 }
 const itemsWithInvalidTerms = formData.selectedBoqItems.filter(item => {
 if (!item.paymentTerms?.length) return false;
 return item.paymentTerms.reduce((s, t) => s + Number(t.percentage || 0), 0) !== 100;
 });
 if (itemsWithInvalidTerms.length > 0) {
 return swal("Invalid Payment Terms", `Item "${itemsWithInvalidTerms[0].itemName}" terms must total 100%`, "warning");
 }

 const cleanedGlobal = filledGlobal.map(t => ({
 name: globalPaymentMode === "CUSTOM" ? (t.name || "").trim() : "",
 stage: globalPaymentMode === "STANDARD" ? (t.name || "").trim() : "",
 percentage: Number(t.percentage || 0),
 }));

 setSubmitting(true);
 const payload = {
 ...formData,
 value: formData.subTotal,
 paymentMode: globalPaymentMode,
 structuredPaymentTerms: cleanedGlobal,
      selectedBoqItems: formData.selectedBoqItems.map(item => ({
        ...item,
        boqItemId: item.isManual ? null : item.boqItemId,
        isManual: Boolean(item.isManual),
        quantity: Number(item.quantity || 0),
        price: Number(item.price || 0),
        amount: Number(item.amount || ((Number(item.quantity) || 0) * (Number(item.price) || 0))),
        paymentTerms: item.paymentTerms || [],
        paymentMode: item.paymentMode || "CUSTOM",
      })),
 };
 
 let res;
 if (id) {
 res = await api.put(`/contractual-work-orders/${id}`, payload);
 } else {
 res = await api.post("/contractual-work-orders", payload);
 }

 if (res.data.success) {
 swal("Success", `Contractual Work Order ${id ? "updated" : "created"}`, "success");
 const listUrl = formData.workOrder
   ? `/contractual-debit?${activeProjectId ? `project=${activeProjectId}&` : ""}workOrder=${formData.workOrder}`
   : `/contractual-debit${activeProjectId ? `?project=${activeProjectId}` : ""}`;
 navigate(listUrl, { replace: true });
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to save", "error");
 } finally {
 setSubmitting(false);
 }
 };

 const activeProjectId = projectFromUrl || currentProject || (selectedWorkOrder?.project?._id || selectedWorkOrder?.project) || "";

 const filteredWorkOrders = useMemo(() => {
  if (!activeProjectId) return workOrders;
  return workOrders.filter((wo) => {
   const pId = wo.project?._id || wo.project;
   return String(pId) === String(activeProjectId) || String(wo._id) === String(formData.workOrder);
  });
 }, [workOrders, activeProjectId, formData.workOrder]);

 const woOptions = filteredWorkOrders.map(wo => ({
    value: wo._id,
    label: `${wo.woNo || wo.woNumber}${wo.label ? ` – ${wo.label}` : ""}${wo.project?.projectName ? ` (${wo.project.projectName})` : ""}`
  }));

 const vendorOptions = vendors.map(v => ({
 value: v._id,
 label: v.name
 }));

 const quillModules = {
 toolbar: [
 [{ 'header': [1, 2, false] }],
 ['bold', 'italic', 'underline', 'strike', 'blockquote'],
 [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
 ['link', 'clean']
 ],
 };

 if (loading) return (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 <p className="mt-3 text-muted">Loading details...</p>
 </div>
 );

 return (
 <Fragment>
 <PageTitle activeMenu={id ? "Edit Contractual WO" : "Add Contractual WO"} motherMenu="Accounts" />
 <Row>
 <Col lg={12}>
 <Form onSubmit={handleSubmit}>
 <Card className="border-0 shadow-sm mb-4">
 <Card.Header className="bg-white border-bottom py-3">
 <div className="d-flex justify-content-between align-items-center w-100">
 <h4 className="mb-0 text-primary fw-bold">
 {id ? "Edit Contractual Work Order" : "New Contractual Work Order"}
 </h4>
 <div className="d-flex gap-2 ms-auto">
 {id && (
 <Button 
 variant="info" 
 size="sm" 
 className="px-3 text-white" 
 onClick={handlePreviewPDF}
 disabled={previewingPdf}
 >
 <i className="fa fa-eye me-2"></i> Preview PDF
 </Button>
 )}
 <Button variant="light" size="sm" className="px-3" onClick={() => navigate(-1)}>
 <i className="fa fa-arrow-left me-2"></i> Back
 </Button>
 </div>
 </div>
 </Card.Header>
 <Card.Body className="p-4">
  {/* 1. Basic Info Section */}
  <div className="mb-5">
  <h5 className="fw-bold mb-4 border-bottom pb-2">Basic Information</h5>
  <Row className="g-3 align-items-center">
  <Col md={4}>
  <Form.Group className="mb-0">
  <Form.Label className="fw-bold small text-muted text-uppercase mb-2">Select Work Order <span className="text-danger">*</span></Form.Label>
  <Select
  options={woOptions}
  value={woOptions.find(o => o.value === formData.workOrder)}
  onChange={(opt) => {
   const selected = workOrders.find((w) => String(w._id) === String(opt?.value));
   const pId = selected?.project?._id || selected?.project;
   if (pId && !projectFromUrl) {
    setCurrentProject(String(pId));
   }
   setFormData({ ...formData, workOrder: opt ? opt.value : "", selectedBoqItems: [] });
  }}
  placeholder="Search Work Order..."
  styles={selectStyles}
  isClearable
  />
  </Form.Group>
  </Col>
  <Col md={4}>
  <Form.Group className="mb-0">
  <Form.Label className="fw-bold small text-muted text-uppercase mb-2">Select Vendor <span className="text-danger">*</span></Form.Label>
  <Select
  options={vendorOptions}
  value={vendorOptions.find(o => o.value === formData.vendor)}
  onChange={(opt) => {
  const vId = opt.value;
  setFormData({ ...formData, vendor: vId });
  calculateTotals(formData.subTotal, vId);
  }}
  placeholder="Search Vendor..."
  styles={selectStyles}
  isClearable
  />
  </Form.Group>
  </Col>
  <Col md={4}>
  <Form.Group className="mb-0">
  <Form.Label className="fw-bold small text-muted text-uppercase mb-2">Contractual Value (Excl. GST) <span className="text-danger">*</span></Form.Label>
  <div className="input-group" style={{ height: "42px" }}>
  <span
    className="input-group-text bg-white border-end-0 text-muted fw-bold"
    style={{
      height: "42px",
      borderColor: "#d7dae3",
      borderTopLeftRadius: "8px",
      borderBottomLeftRadius: "8px",
      borderTopRightRadius: "0px",
      borderBottomRightRadius: "0px",
      paddingLeft: "14px",
      paddingRight: "10px",
      fontSize: "1rem"
    }}
  >
    ₹
  </span>
  <Form.Control
    type="number"
    placeholder="Enter base value"
    value={formData.subTotal}
    onChange={(e) => {
      const val = e.target.value;
      calculateTotals(val, formData.vendor);
    }}
    className="fw-bold border-start-0"
    style={{
      height: "42px",
      borderColor: "#d7dae3",
      borderTopLeftRadius: "0px",
      borderBottomLeftRadius: "0px",
      borderTopRightRadius: "8px",
      borderBottomRightRadius: "8px",
      fontSize: "0.95rem",
      boxShadow: "none"
    }}
  />
</div>
  </Form.Group>
  </Col>
  </Row>
  </div>

 {/* Billing & Shipping Details */}
 <div className="mb-5">
 <h5 className="fw-bold mb-4 border-bottom pb-2">Billing & Shipping Details</h5>
 <Row>
 <Col md={6} className="mb-4">
 <div className="border rounded p-3 h-100">
 <h6 className="text-primary fw-bold mb-3 text-uppercase">Manual Bill To</h6>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">Name</Form.Label>
 <Form.Control
 type="text"
 value={formData.manualBillTo.name}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualBillTo: { ...prev.manualBillTo, name: e.target.value },
 }))}
 placeholder="Bill to name"
 />
 </Form.Group>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">Address</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 value={formData.manualBillTo.address}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualBillTo: { ...prev.manualBillTo, address: e.target.value },
 }))}
 placeholder="Billing address"
 />
 </Form.Group>
 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">GST Number</Form.Label>
 <Form.Control
 type="text"
 value={formData.manualBillTo.gstNumber}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualBillTo: { ...prev.manualBillTo, gstNumber: e.target.value },
 }))}
 placeholder="GSTIN"
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">Phone</Form.Label>
 <Form.Control
 type="text"
 value={formData.manualBillTo.phone}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualBillTo: { ...prev.manualBillTo, phone: e.target.value },
 }))}
 placeholder="Phone number"
 />
 </Form.Group>
 </Col>
 </Row>
 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">State</Form.Label>
 <Form.Control
 type="text"
 value={formData.manualBillTo.state}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualBillTo: { ...prev.manualBillTo, state: e.target.value },
 }))}
 placeholder="State"
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">State Code</Form.Label>
 <Form.Control
 type="text"
 value={formData.manualBillTo.stateCode}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualBillTo: { ...prev.manualBillTo, stateCode: e.target.value },
 }))}
 placeholder="State code"
 />
 </Form.Group>
 </Col>
 </Row>
 </div>
 </Col>
 <Col md={6} className="mb-4">
 <div className="border rounded p-3 h-100">
 <h6 className="text-primary fw-bold mb-3 text-uppercase">Manual Ship To</h6>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">Name</Form.Label>
 <Form.Control
 type="text"
 value={formData.manualShipTo.name}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualShipTo: { ...prev.manualShipTo, name: e.target.value },
 }))}
 placeholder="Ship to name"
 />
 </Form.Group>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">Address</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 value={formData.manualShipTo.address}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualShipTo: { ...prev.manualShipTo, address: e.target.value },
 }))}
 placeholder="Shipping address"
 />
 </Form.Group>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">Project Name</Form.Label>
 <Form.Control
 type="text"
 value={formData.manualShipTo.projectName}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualShipTo: { ...prev.manualShipTo, projectName: e.target.value },
 }))}
 placeholder="Project name"
 />
 </Form.Group>
 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">State</Form.Label>
 <Form.Control
 type="text"
 value={formData.manualShipTo.state}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualShipTo: { ...prev.manualShipTo, state: e.target.value },
 }))}
 placeholder="State"
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label className="small text-muted text-uppercase">State Code</Form.Label>
 <Form.Control
 type="text"
 value={formData.manualShipTo.stateCode}
 onChange={(e) => setFormData((prev) => ({
 ...prev,
 manualShipTo: { ...prev.manualShipTo, stateCode: e.target.value },
 }))}
 placeholder="State code"
 />
 </Form.Group>
 </Col>
 </Row>
 </div>
 </Col>
 </Row>
 </div>

 {/* 2. BOQ Section */}
 {(formData.workOrder || selectedWorkOrder) && (
 <div className="mb-5">
 <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
 <h5 className="fw-bold mb-0">Scope of Work (BOQ & Manual Items)</h5>
 <div className="d-flex gap-2">
 <Button
 variant={globalPaymentTotal === 100 ? "warning" : "outline-warning"}
 size="sm"
 onClick={() => setShowGlobalTermsModal(true)}
 title="Set global payment terms for all items"
 >
 <i className="fas fa-rupee-sign me-1"></i>
 Payment Terms
 {globalPaymentTotal === 100 && <i className="fas fa-check-circle ms-1 text-success"></i>}
 </Button>
 {selectedWorkOrder?.boqItems?.length > 0 && (
  <Button variant="outline-success" size="sm" className="rounded-pill" onClick={() => handleSelectAllBoq(true)}>
   Select All Items
  </Button>
 )}
 <Button variant="outline-primary" size="sm" className="rounded-pill" onClick={handleAddManualItem}>
 <i className="fas fa-plus me-1"></i> Add Manual Item
 </Button>
 </div>
 </div>
 
 {selectedWorkOrder?.boqItems?.length > 0 && (
 <div className="mb-4">
 <Select
 isMulti
 options={selectedWorkOrder.boqItems.map(item => ({
 value: item._id,
 label: `${item.itemName} (Available: ${item.quantity} ${item.unit})`,
 item: item
 }))}
 value={formData.selectedBoqItems.filter(i => !i.isManual).map(i => ({
 value: i.boqItemId,
 label: `${i.itemName} (Available: ${i.quantity} ${i.unit})`
 }))}
 onChange={(selectedOptions) => {
 const manualItems = formData.selectedBoqItems.filter(i => i.isManual);
 const updatedBoqItems = selectedOptions.map(opt => {
 const existing = formData.selectedBoqItems.find(i => i.boqItemId === opt.value);
 if (existing) return existing;
 
 const hasItemTerms = Array.isArray(opt.item.paymentTerms) && opt.item.paymentTerms.length > 0;
 const paymentMode = hasItemTerms ? (opt.item.paymentMode || "CUSTOM") : globalPaymentMode;
 const sourceTerms = hasItemTerms ? opt.item.paymentTerms : globalPaymentTerms;
 
 const paymentTerms = sourceTerms.map(t => ({
 stage: paymentMode === "STANDARD" ? (t.stage || t.name || "") : "",
 name: paymentMode === "CUSTOM" ? (t.name || t.stage || "") : "",
 percentage: t.percentage || 0,
 }));

 return {
 boqItemId: opt.item._id,
 isManual: false,
 wlino: opt.item.wlino,
 itemName: opt.item.itemName,
 description: opt.item.description,
 unit: opt.item.unit,
 quantity: opt.item.quantity,
 price: opt.item.price,
 amount: (Number(opt.item.quantity) || 0) * (Number(opt.item.price) || 0),
 paymentMode: paymentMode,
 paymentTerms: paymentTerms,
 };
 });
 const allSelected = [...updatedBoqItems, ...manualItems];
 const newSubTotal = allSelected.reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.price || 0)), 0);
 setFormData(prev => ({ ...prev, selectedBoqItems: allSelected }));
 calculateTotals(newSubTotal, formData.vendor);
 }}
 placeholder="Search and add BOQ items..."
 />
 </div>
 )}

 {formData.selectedBoqItems.length === 0 && (
 <div className="alert alert-light border text-center py-4 text-muted">
  No items added yet. Click <strong>+ Add Manual Item</strong> {selectedWorkOrder?.boqItems?.length > 0 ? "or select from BOQ items above" : ""} to add scope items.
 </div>
 )}

 {formData.selectedBoqItems.length > 0 && (
 <div className="table-responsive border rounded shadow-sm">
 <Table hover className="mb-0 align-middle">
 <thead className="table-light">
 <tr className="text-muted small">
 <th className="text-center py-2" style={{ width: "45px" }}>#</th>
 <th className="py-2" style={{ width: "115px" }}>WLI NO</th>
 <th className="py-2">ITEM & SPECIFICATION</th>
 <th className="text-center py-2" style={{ width: "95px" }}>UOM</th>
 <th className="text-end py-2" style={{ width: "105px" }}>QUANTITY</th>
 <th className="text-end py-2" style={{ width: "115px" }}>RATE (₹)</th>
 <th className="text-end py-2" style={{ width: "125px" }}>AMOUNT (₹)</th>
 <th className="text-center py-2" style={{ width: "95px" }}>TERMS</th>
 <th className="text-center py-2" style={{ width: "50px" }}></th>
 </tr>
 </thead>
 <tbody>
 {formData.selectedBoqItems.map((item, index) => {
 const itemAmt = (Number(item.quantity) || 0) * (Number(item.price) || 0);
 const totalTermsPct = (item.paymentTerms || []).reduce((s, t) => s + Number(t.percentage || 0), 0);
 const hasTerms = (item.paymentTerms || []).length > 0;

 return (
 <tr key={item.boqItemId || index}>
 <td className="text-center text-muted small fw-semibold">
 {index + 1}
 </td>
 <td>
 {item.isManual ? (
 <div>
 <span className="badge bg-primary-subtle text-primary border border-primary-subtle mb-1 d-inline-block px-2 py-0.5" style={{ fontSize: "10px" }}>
 Manual
 </span>
 <Form.Control
 size="sm"
 placeholder="Code"
 value={item.wlino || ""}
 onChange={(e) => handleItemEdit(index, "wlino", e.target.value)}
 className="font-monospace small py-1"
 style={{ maxWidth: "95px" }}
 />
 </div>
 ) : (
 <span className="fw-semibold font-monospace small">
 {item.wlino || "-"}
 </span>
 )}
 </td>
 <td>
 {item.isManual ? (
 <div className="d-flex flex-column gap-1">
 <Form.Control
 size="sm"
 placeholder="Item Name / Heading *"
 value={item.itemName || ""}
 onChange={(e) => handleItemEdit(index, "itemName", e.target.value)}
 className="fw-bold py-1"
 required
 />
 <Form.Control
 size="sm"
 as="textarea"
 rows={1}
 placeholder="Description or specification (optional)"
 value={item.description || ""}
 onChange={(e) => handleItemEdit(index, "description", e.target.value)}
 className="small text-muted py-1"
 style={{ resize: "vertical" }}
 />
 </div>
 ) : (
 <div>
 <div className="fw-bold">{item.itemName}</div>
 {item.description && (
 <div className="text-muted small mt-0.5" style={{ fontSize: "12px", lineHeight: "1.35", whiteSpace: "pre-line" }}>
 {item.description}
 </div>
 )}
 </div>
 )}
 </td>
 <td className="text-center">
 {item.isManual ? (
 <Form.Select
 size="sm"
 value={item.unit || "Nos"}
 onChange={(e) => handleItemEdit(index, "unit", e.target.value)}
 className="text-center py-1 font-monospace small"
 >
 <option value="Nos">Nos</option>
 <option value="RM">RM</option>
 <option value="Sqm">Sqm</option>
 <option value="Set">Set</option>
 <option value="Kg">Kg</option>
 <option value="Mtr">Mtr</option>
 <option value="Pkt">Pkt</option>
 <option value="Lot">Lot</option>
 <option value="Pcs">Pcs</option>
 <option value="Points">Points</option>
 </Form.Select>
 ) : (
 <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2 py-1">
 {item.unit || "Nos"}
 </span>
 )}
 </td>
 <td>
 <Form.Control
 type="number"
 size="sm"
 min="0"
 step="any"
 value={item.quantity}
 onChange={(e) => handleItemEdit(index, "quantity", e.target.value)}
 className="text-end fw-semibold font-monospace py-1"
 placeholder="0"
 />
 </td>
 <td>
 <Form.Control
 type="number"
 size="sm"
 min="0"
 step="any"
 value={item.price}
 onChange={(e) => handleItemEdit(index, "price", e.target.value)}
 className="text-end fw-semibold font-monospace py-1"
 placeholder="0.00"
 />
 </td>
 <td className="text-end">
 <div className="fw-bold text-success font-monospace" style={{ fontSize: "13px" }}>
 ₹{itemAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
 </div>
 </td>
 <td className="text-center">
 <div className="d-flex align-items-center justify-content-center">
 <Button
 variant={hasTerms ? "warning" : "outline-secondary"}
 size="sm"
 className="px-2 py-0.5 d-inline-flex align-items-center"
 title={hasTerms
 ? item.paymentTerms.map(t => `${t.stage || t.name || ""}: ${t.percentage || 0}%`).join(", ")
 : "No terms — click to set"}
 onClick={() => openItemTerms(index)}
 >
 <i className="fas fa-rupee-sign me-1" style={{ fontSize: "10px" }}></i>
 <span style={{ fontSize: "11px", fontWeight: "600" }}>
 {hasTerms ? `${totalTermsPct}%` : "Set"}
 </span>
 </Button>
 {hasTerms && (
 <button
 type="button"
 className="btn btn-link btn-sm text-danger p-0 ms-1 text-decoration-none"
 onClick={() => clearItemTerms(index)}
 title="Clear terms"
 >
 <i className="fas fa-times-circle"></i>
 </button>
 )}
 </div>
 </td>
 <td className="text-center">
 <button
 type="button"
 className="btn btn-sm btn-outline-danger border-0 p-1 rounded-circle"
 title="Delete line item"
 onClick={() => handleRemoveItem(index)}
 >
 <i className="fas fa-trash-alt"></i>
 </button>
 </td>
 </tr>
 );
 })}
 </tbody>
 <tfoot className="border-top">
 <tr className="bg-light-subtle">
 <td colSpan="4" className="text-muted small py-2">
 Total Items: <strong>{formData.selectedBoqItems.length}</strong>
 </td>
 <td colSpan="2" className="text-end fw-bold py-2 text-muted small text-uppercase">
 Sub Total:
 </td>
 <td className="text-end fw-bold text-success font-monospace py-2" style={{ fontSize: "14px" }}>
 ₹{formData.selectedBoqItems
 .reduce((acc, it) => acc + ((Number(it.quantity) || 0) * (Number(it.price) || 0)), 0)
 .toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
 </td>
 <td colSpan="2"></td>
 </tr>
 </tfoot>
 </Table>
 </div>
 )}
 </div>
 )}

 {/* 3. Detailed Info Section */}
 <div className="mb-5">
 <h5 className="fw-bold mb-4 border-bottom pb-2">Scope & Specifications</h5>
 <Row>
 <Col md={6} className="mb-4">
 <Form.Label className="fw-bold small text-muted text-uppercase">Technical Specifications</Form.Label>
 <ReactQuill
 theme="snow"
 value={formData.workDetails}
 onChange={(content) => setFormData({ ...formData, workDetails: content })}
 modules={quillModules}
 placeholder="Detailed scope, technical specifications, etc..."
 style={{ height: '250px', marginBottom: '50px' }}
 />
 </Col>
 <Col md={6} className="mb-4">
 <Form.Label className="fw-bold small text-muted text-uppercase">Terms & Conditions</Form.Label>
 <ReactQuill
 theme="snow"
 value={formData.paymentTerms}
 onChange={(content) => setFormData({ ...formData, paymentTerms: content })}
 modules={quillModules}
 placeholder="Payment schedule, retention, mobilization advance, etc..."
 style={{ height: '250px', marginBottom: '50px' }}
 />
 </Col>
 </Row>
 </div>

 {/* 4. Financials & Remarks */}
 <div className="mb-5 mt-4">
 <h5 className="fw-bold mb-4 border-bottom pb-2">Financials & Remarks</h5>
 <Row className="g-4 align-items-stretch">
 <Col md={6}>
 <div className="border rounded-3 p-4 bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
 <div>
 <h6 className="fw-bold mb-3 text-primary d-flex align-items-center">
 <i className="fas fa-file-invoice-dollar me-2"></i> Cost Breakdown
 </h6>
 <div className="d-flex justify-content-between mb-3 align-items-center pb-2 border-bottom">
 <span className="text-muted fw-semibold">Base Amount:</span>
 <span className="fw-bold fs-5 text-dark">₹{(formData.subTotal || 0).toLocaleString()}</span>
 </div>

 <div className="mb-3">
 <div className="d-flex justify-content-between align-items-center mb-2">
 <span className="small text-muted fw-semibold">GST Applied Automatically:</span>
 <span className="badge bg-light text-primary border">
 {formData.cgst + formData.sgst + formData.igst}%
 </span>
 </div>

 {(formData.cgst > 0 || formData.sgst > 0 || formData.igst > 0) && (
 <div className="p-3 rounded-2 border mb-2 bg-light-subtle">
 {formData.cgst > 0 && (
 <div className="d-flex justify-content-between mb-1 small">
 <span className="text-muted">CGST ({formData.cgst}%):</span>
 <span className="fw-semibold">₹{(formData.cgstAmount || 0).toLocaleString()}</span>
 </div>
 )}
 {formData.sgst > 0 && (
 <div className="d-flex justify-content-between mb-1 small">
 <span className="text-muted">SGST ({formData.sgst}%):</span>
 <span className="fw-semibold">₹{(formData.sgstAmount || 0).toLocaleString()}</span>
 </div>
 )}
 {formData.igst > 0 && (
 <div className="d-flex justify-content-between mb-1 small">
 <span className="text-muted">IGST ({formData.igst}%):</span>
 <span className="fw-semibold">₹{(formData.igstAmount || 0).toLocaleString()}</span>
 </div>
 )}
 </div>
 )}

 <div className="d-flex justify-content-between align-items-center pt-2">
 <span className="text-muted fw-semibold">Total Tax:</span>
 <span className="fw-bold text-dark">₹{(formData.taxAmount || 0).toLocaleString()}</span>
 </div>
 </div>
 </div>

 <div className="d-flex justify-content-between align-items-center pt-3 border-top border-2 mt-4">
 <h5 className="text-primary fw-bold mb-0">Total Contract Value:</h5>
 <h4 className="text-primary fw-bold mb-0">₹{(formData.totalAmount || 0).toLocaleString()}</h4>
 </div>
 </div>
 </Col>
 <Col md={6}>
 <div className="border rounded-3 p-4 bg-white shadow-sm h-100 d-flex flex-column">
 <h6 className="fw-bold mb-3 text-primary d-flex align-items-center">
 <i className="fas fa-sticky-note me-2"></i> Internal Remarks
 </h6>
 <Form.Group className="d-flex flex-column flex-grow-1 mb-0">
 <Form.Control
 as="textarea"
 placeholder="Notes for internal review..."
 value={formData.remarks}
 onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
 className="flex-grow-1 border shadow-none"
 style={{ resize: "none", minHeight: "220px" }}
 />
 </Form.Group>
 </div>
 </Col>
 </Row>
 </div>

 {/* 5. Signatures */}
 <div className="mb-5 mt-4">
 <h5 className="fw-bold mb-4 border-bottom pb-2">Document Authentication</h5>
 <Row>
 <Col md={4} className="mb-3">
 <div className="border rounded p-4 text-center bg-white h-100 shadow-sm border-dashed">
 <div className="fw-bold mb-3 text-muted text-uppercase small">Prepared By</div>
 {formData.preparedBySign?.sign ? (
 <div className="position-relative d-inline-block mx-auto mb-3">
 <img src={formData.preparedBySign.sign} alt="Sign" className="img-fluid" style={{ maxHeight: '80px', border: '1px solid #eee' }} />
 <Button variant="danger" size="sm" className="position-absolute top-0 end-0 rounded-circle" style={{ padding: '0 6px', fontSize: '12px', marginTop: '-10px', marginRight: '-10px' }} onClick={() => setFormData({...formData, preparedBySign: null})}>&times;</Button>
 </div>
 ) : (
 <div className="py-4 text-muted small border rounded mb-3 bg-light opacity-50">No Signature</div>
 )}
 <Button variant="outline-primary" size="sm" className="w-100 rounded-pill" onClick={() => { setSignType("prepared"); setShowSignModal(true); }}>
 <i className="fas fa-pen-nib me-2"></i>Sign Document
 </Button>
 <div className="small text-muted mt-2">Project Coordinator</div>
 </div>
 </Col>
 </Row>
 </div>

 <div className="text-end mt-5 border-top pt-4">
 <Button variant="secondary" size="lg" className="me-2 px-4 rounded-pill" onClick={() => navigate(-1)}>
 Cancel
 </Button>
 <Button variant="primary" type="submit" disabled={submitting} size="lg" className="px-5 rounded-pill shadow">
 {submitting ? (
 <><Spinner animation="border" size="sm" className="me-2" /> Saving...</>
 ) : (
 id ? "Update Work Order" : "Generate Work Order"
 )}
 </Button>
 </div>
 </Card.Body>
 </Card>
 </Form>
 <SignatureModal
 show={showSignModal}
 onHide={() => setShowSignModal(false)}
 onSave={handleSaveSignature}
 title={`Signature Capture`}
 />

 {/* Global Payment Terms Modal */}
 <Modal show={showGlobalTermsModal} onHide={() => setShowGlobalTermsModal(false)} centered size="lg">
 <Modal.Header closeButton className="border-bottom">
 <Modal.Title className="fw-bold fs-6">
 <i className="fas fa-rupee-sign text-warning me-2"></i>
 Global Payment Terms
 <small className="text-muted fw-normal ms-2" style={{ fontSize: 12 }}>Applies to all items without individual terms</small>
 </Modal.Title>
 </Modal.Header>
 <Modal.Body className="p-4">
 <div className="mb-3 d-flex align-items-center gap-2">
 <Form.Label className="mb-0 fw-semibold">Mode:</Form.Label>
 <Form.Select size="sm" style={{ width: 180 }} value={globalPaymentMode}
 onChange={(e) => { setGlobalPaymentMode(e.target.value); setGlobalPaymentTerms([{ name: "", percentage: "" }]); }}>
 <option value="CUSTOM">Custom Milestones</option>
 <option value="STANDARD">Standard Stages</option>
 </Form.Select>
 </div>
 <table className="table table-bordered align-middle mb-2">
 <thead className="table-light">
 <tr><th style={{ width: "5%" }}>#</th><th>Stage / Milestone</th><th style={{ width: "160px" }} className="text-center">%</th><th style={{ width: "40px" }}></th></tr>
 </thead>
 <tbody>
 {globalPaymentTerms.map((row, i) => (
 <tr key={i}>
 <td className="text-center text-muted">{i + 1}</td>
 <td>
 {globalPaymentMode === "STANDARD" ? (
 <Form.Select size="sm" value={row.name} className="border-0 shadow-none"
 onChange={(e) => setGlobalPaymentTerms(prev => prev.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))}>
 <option value="">Select Stage</option>
 {PAYMENT_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
 </Form.Select>
 ) : (
 <Form.Control size="sm" type="text" placeholder="e.g. 30% Advance..." className="border-0 shadow-none"
 value={row.name} onChange={(e) => setGlobalPaymentTerms(prev => prev.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))} />
 )}
 </td>
 <td><Form.Control size="sm" type="number" min="0" max="100" className="border-0 shadow-none text-center fw-bold"
 value={row.percentage} onChange={(e) => setGlobalPaymentTerms(prev => prev.map((r, idx) => idx === i ? { ...r, percentage: e.target.value } : r))} /></td>
 <td className="text-center">
 <Button variant="link" size="sm" className="text-danger p-0" disabled={globalPaymentTerms.length === 1}
 onClick={() => setGlobalPaymentTerms(prev => prev.filter((_, idx) => idx !== i))}>
 <i className="fas fa-times"></i>
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 <div className="d-flex justify-content-between align-items-center">
 <Button variant="link" className="p-0 text-primary text-decoration-none fw-bold"
 onClick={() => setGlobalPaymentTerms(prev => [...prev, { name: "", percentage: "" }])}>+ Add Row</Button>
 <span className={`fw-bold ${globalPaymentTotal === 100 ? "text-success" : globalPaymentTotal > 0 ? "text-danger" : "text-muted"}`}>
 Total: {globalPaymentTotal}% {globalPaymentTotal === 100 && <i className="fas fa-check-circle"></i>}
 </span>
 </div>
 </Modal.Body>
 <Modal.Footer className="bg-light">
 <Button variant="secondary" onClick={() => setShowGlobalTermsModal(false)}>Cancel</Button>
 <Button variant="primary" disabled={globalPaymentTotal !== 100} onClick={() => setShowGlobalTermsModal(false)}>Save Global Terms</Button>
 </Modal.Footer>
 </Modal>

 {/* Per-Item Payment Terms Modal */}
 <Modal show={showItemTermsModal} onHide={() => setShowItemTermsModal(false)} centered size="lg">
 <Modal.Header closeButton className="border-bottom">
 <Modal.Title className="fw-bold fs-6">
 <i className="fas fa-rupee-sign text-warning me-2"></i>
 Item Payment Terms — {itemTermsIndex !== null ? formData.selectedBoqItems[itemTermsIndex]?.itemName || `Item ${itemTermsIndex + 1}` : ""}
 </Modal.Title>
 </Modal.Header>
 <Modal.Body className="p-4">
 <div className="mb-3 d-flex align-items-center gap-2">
 <Form.Label className="mb-0 fw-semibold">Mode:</Form.Label>
 <Form.Select size="sm" style={{ width: 180 }} value={itemTermsMode}
 onChange={(e) => {
 const newMode = e.target.value;
 setItemTermsMode(newMode);
 // Preserve existing terms when switching modes, just normalize them
 setItemTerms(itemTerms.map(t => ({
 name: newMode === "CUSTOM" ? (t.name || t.stage || "") : (t.stage || t.name || ""),
 percentage: t.percentage || "",
 })));
 }}>
 <option value="CUSTOM">Custom Milestones</option>
 <option value="STANDARD">Standard Stages</option>
 </Form.Select>
 </div>
 <table className="table table-bordered align-middle mb-2">
 <thead className="table-light">
 <tr><th style={{ width: "5%" }}>#</th><th>Stage / Milestone</th><th style={{ width: "160px" }} className="text-center">%</th><th style={{ width: "40px" }}></th></tr>
 </thead>
 <tbody>
 {itemTerms.map((row, i) => (
 <tr key={i}>
 <td className="text-center text-muted">{i + 1}</td>
 <td>
 {itemTermsMode === "STANDARD" ? (
 <Form.Select size="sm" value={row.name} className="border-0 shadow-none"
 onChange={(e) => setItemTerms(prev => prev.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))}>
 <option value="">Select Stage</option>
 {PAYMENT_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
 </Form.Select>
 ) : (
 <Form.Control size="sm" type="text" placeholder="e.g. 30% Advance..." className="border-0 shadow-none"
 value={row.name} onChange={(e) => setItemTerms(prev => prev.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))} />
 )}
 </td>
 <td><Form.Control size="sm" type="number" min="0" max="100" className="border-0 shadow-none text-center fw-bold"
 value={row.percentage} onChange={(e) => setItemTerms(prev => prev.map((r, idx) => idx === i ? { ...r, percentage: e.target.value } : r))} /></td>
 <td className="text-center">
 <Button variant="link" size="sm" className="text-danger p-0" disabled={itemTerms.length === 1}
 onClick={() => setItemTerms(prev => prev.filter((_, idx) => idx !== i))}>
 <i className="fas fa-times"></i>
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 <div className="d-flex justify-content-between align-items-center">
 <Button variant="link" className="p-0 text-primary text-decoration-none fw-bold"
 onClick={() => setItemTerms(prev => [...prev, { name: "", percentage: "" }])}>+ Add Row</Button>
 <span className={`fw-bold ${itemTermsTotal === 100 ? "text-success" : itemTermsTotal > 0 ? "text-danger" : "text-muted"}`}>
 Total: {itemTermsTotal}% {itemTermsTotal === 100 && <i className="fas fa-check-circle"></i>}
 </span>
 </div>
 </Modal.Body>
 <Modal.Footer className="bg-light">
 <Button variant="secondary" onClick={() => setShowItemTermsModal(false)}>Cancel</Button>
 <Button variant="primary" onClick={saveItemTerms} disabled={itemTermsTotal !== 100}>Save Item Terms</Button>
 </Modal.Footer>
 </Modal>

 {/* PDF Loader Overlay */}
 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing CWO PDF…</h6>
 </div>
 </div>
 )}
 </Col>
 </Row>
 </Fragment>
 );
};

export default ContractualWorkOrderAdd;
